import React, { useState, useEffect, useRef, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  IStringHash,
} from "@models";
import { 
  Character,
  CharacterItem,
  CharacterContainer,
  // CharacterFeatureSource,
  CharacterAbility,
  Ability,
  CharacterAction,
  CharacterSpell,
  Attack,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import StringBox from "@components/input/strings/StringBox";
import FloatMenu from "@components/input/menus/FloatMenu";

import CharacterCurrency from "@dndComponents/character/inventory/CharacterCurrency";
import CharacterStartingEquipment from "@dndComponents/character/inventory/startingEquipment/CharacterStartingEquipment";

import CharacterViewInventoryItem from "@dndComponents/character/inventory/CharacterViewInventoryItem";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterAttunement from "@dndComponents/character/inventory/CharacterAttunement";
import CharacterActionInput from "@dndComponents/character/spells_and_abilities/CharacterActionInput";
import CharacterItemDetailsSidebar from "@dndComponents/character/inventory/CharacterItemDetailsSidebar";
import CharacterManageInventoryButton from "@dndComponents/character/inventory/manage/CharacterManageInventoryButton";
import CharacterSpellDetails from "@dndComponents/character/spells_and_abilities/CharacterSpellDetails";
import CharacterAttackSidebar from "@dndComponents/character/inventory/CharacterAttackSidebar";

interface Props {
  character: Character;
  disabled: boolean;
  shared: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterViewInventory({
  character,
  disabled,
  shared,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [search_string, setSearchString] = useState("");
  const [view, setView] = useState("ALL");
  const [edit_view, setEditView] = useState(false);
  const [drawer, setDrawer] = useState("");
  const [details_item, setDetailsItem] = useState<CharacterItem | null>(null);
  const [details_action, setDetailsAction] = useState<CharacterAction | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const itemsChanged = useCallback(() => { 
    console.log("Char View Inventory - Items Changed");
    setTimeout(() => {
      setReloading(true);
    }, 100);
  }, []);

  useEffect(() => {
    console.log("Char View Inventory");
    character.subscription_holder.subscribe("Items", mounted, itemsChanged);
  }, [character, itemsChanged]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderCurrency = () => {
    return (
      <div
        style={{
          float: "right",
          fontSize: "13px",
          fontWeight: "bold",
          cursor: "pointer" 
        }} 
        onClick={() => {
          setDrawer("currency");
        }}>
        Currency: { character.money.toString() }
      </div>
    )
  }

  const renderInventory = () => {
    // Breaking this up into containers and equipment
    const filtered = api.filter("", character.items, { search_string });
    return (
      <>
        <Grid
          style={{
            fontSize: "14px",
          }}
          className="header"
          size={{
            xs: 12
          }}>
          &nbsp;Inventory
        </Grid>
        <>
          <Grid
            style={{
              fontWeight: "bold"
            }}
            size={{
              xs: 2
            }}>
            &nbsp;Active
          </Grid>
          <Grid
            style={{
              fontWeight: "bold"
            }}
            size={{
              xs: 6
            }}>
            Name
          </Grid>
          <Grid
            style={{
              fontWeight: "bold"
            }}
            size={{
              xs: 1
            }}>
            Weight
          </Grid>
          <Grid
            style={{
              fontWeight: "bold"
            }}
            size={{
              xs: 1
            }}>
            Qty
          </Grid>
          <Grid
            style={{
              fontWeight: "bold"
            }}
            size={{
              xs: 1
            }}>
            Cost (GP)
          </Grid>
          <Grid
            style={{
              fontWeight: "bold"
            }}
            size={{
              xs: 1
            }}>
            Worn Type
          </Grid>
        </>
        { character.containers.filter(o => o.true_id === "Equipment" &&
          ["ALL","Inventory",o.true_id].includes(view)).sort((a,b) => {return a.name.localeCompare(b.name)}).map((cont, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              { renderContainer(cont, filtered) }
            </Grid>
          );
        })}
        { character.containers.filter(o => !["Equipment","Storage"].includes(o.true_id) &&
          ["ALL","Inventory",o.true_id].includes(view)).sort((a,b) => {return a.name.localeCompare(b.name)}).map((cont, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              { renderContainer(cont, filtered) }
            </Grid>
          );
        })}
        { character.containers.filter(o => o.true_id === "Storage" &&
          ["ALL","Inventory",o.true_id].includes(view)).sort((a,b) => {return a.name.localeCompare(b.name)}).map((cont, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              { renderContainer(cont, filtered) }
            </Grid>
          );
        })}
      </>
    );
  }

  const renderContainer = (cont: CharacterContainer, 
    filtered: CharacterItem[]) => {
    // Breaking this up into containers and equipment
    const option_map: IStringHash = {};
    character.containers.filter(o => cont.true_id !== o.true_id).forEach(cont2 => {
      option_map[cont2.name] = cont2.true_id;
    });
    // console.log(cont);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <CharacterViewInventoryItem
            character={character}
            item={cont}
            disabled={disabled}
            // log_it={!!cont.base_item}
            onOpen={() => {
              setDetailsItem(cont);
              setDrawer("details");
            }}
          />
        </Grid>
        { filtered.filter(o => 
          o.in_char_container_id === cont.true_id && 
          o.item_type !== "Container").sort((a,b) => {return a.name.localeCompare(b.name)}).map((item) => {
          // console.log(item);
          return (
            <Grid
              key={item.true_id}
              size={{
                xs: 12
              }}>
              <CharacterViewInventoryItem
                character={character}
                item={item}
                disabled={disabled}
                onOpen={() => {
                  setDetailsItem(item);
                  setDrawer("details");
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  }

  const renderSpecial = () => {
    // debugger;
    const filtered = character.actions.filter(o => 
      o.action instanceof CharacterAbility &&
      o.action.the_ability instanceof Ability &&
      o.action.the_ability.show_in.includes("Inventory"));
    return (
      <Grid size={12}
        container
        spacing={0}
        direction="row">
        <Grid size={12}
          style={{
            fontSize: "14px",
          }}
          className={`header`}>
          &nbsp;Special
        </Grid>
        { filtered.map((action) => {
          // console.log(action);
          if (reloading) {
            return (<span key={action.true_id}></span>);
          } else {
            return (
              <Grid
                key={action.true_id}
                size={{
                  xs: 12
                }}>
                <CharacterActionInput
                  char={character}
                  char_interface={character}
                  action={action}
                  group={action.casting_time}
                  // show_extra_effects={true}
                  processing={false}
                  shared={false}
                  level={action.level}
                  // onCharChange={() => {
                  //   onChange();
                  //   onReload(true);
                  // }}
                  // onItemChange={onChange}
                  // onTransform={onTransform}
                  onOpen={() => {
                    setDetailsAction(action);
                    setDrawer("details");
                  }}
                />
              </Grid>
            );
          }
        })}
      </Grid>
    );
  }

  const renderOtherPossessions = () => {
    const enabled = !disabled && api.user !== null && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id));
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          style={{
            fontSize: "12px",
            fontWeight: "bold",
          }}
          size={{
            xs: 12
          }}>
          &nbsp;Other Possessions
        </Grid>
        { edit_view ? 
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name=""
              multiline
              disabled={ !enabled }
              value={ character.other_possessions }
              onBlur={(changed: string) => {
                setEditView(false);
                character.other_possessions = changed;
                character.subscription_holder.itemsChanged();
              }}
            />
          </Grid>
        :
          <Grid
            onClick={() => {
              setEditView(true);
            }}
            size={{
              xs: 12
            }}>
            { character.other_possessions }
          </Grid>
        }
      </Grid>
    );
  }

  const enabled = !disabled && api.user !== null && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id));
  
  const renderExtras = () => {
    const action = details_action;
    const open = drawer === "details";
    if (action && open) {
      // console.log(action);
      let char_item: CharacterItem | undefined = undefined;
      if (action.action instanceof CharacterItem) {
        char_item = action.action;
      } else {
        if (action.action instanceof CharacterAbility && action.action.item_id !== "") {
          const item_id = action.action.item_id;
          char_item = character.items.find(o => o.true_id === item_id);
        }
      }
      return (
        <>
          <ModalOrDrawer
            open={ drawer === "details" } 
            onClose={() => {
              setDrawer("");
              setDetailsAction(null);
              character.subscription_holder.modalClosed();
            }}>
            { char_item ? 
              <CharacterItemDetailsSidebar
                character={character}
                char_item={char_item}
                shared={shared}
                onRemove={() => {
                  setDrawer("");
                  setDetailsAction(null);
                }}
                // onTransform={onTransform}
              /> 
            : (action.action instanceof CharacterSpell || 
              action.action instanceof CharacterAbility) ? 
              <CharacterSpellDetails
                character={character}
                char_interface={character}
                obj={ action.action }
                level={ action.level }
                type={action.type}
                action={action}
                shared={false}
                show_item
                onObjChange={(obj: CharacterSpell | CharacterAbility) => {
                }}
                onClose={() => {
                  setDrawer("");
                  setDetailsAction(null);
                  // setLevel(null);
                }}
                // onTransform={() => {
                //   onTransform();
                // }}
                // onSourceChange={onCharChange}
              /> 
            : action.action instanceof Attack && 
              <CharacterAttackSidebar
                character={character}
                attack={action.action}
              />
            }
          </ModalOrDrawer>
        </>
      );
    } else {
      return (
        <>
          <ModalOrDrawer
            open={ drawer === "currency" } 
            onClose={() => {
              setDrawer("");
              setDetailsAction(null);
              character.subscription_holder.modalClosed();
            }}>
            <CharacterCurrency
              character={character}
            />
          </ModalOrDrawer>
          <ModalOrDrawer
            open={ drawer === "details" } 
            // drawer_only
            onClose={() => {
              setDrawer("");
              setDetailsAction(null);
              character.subscription_holder.modalClosed();
            }}>
            { drawer === "details" && details_item &&
              <CharacterItemDetailsSidebar
                character={character}
                char_item={details_item}
                shared={shared}
                onRemove={() => {
                  setDrawer("");
                  setDetailsAction(null);
                }}
                // onTransform={onTransform}
              />
            }
          </ModalOrDrawer>
        </>
      );
    }
  }
  
  let menu_option_map: IStringHash = {
    "ALL": "ALL",
    "Inventory": "Inventory",
  };
  character.containers.forEach(c => {
    menu_option_map[c.name] = c.true_id;
  });
  menu_option_map = {
    ...menu_option_map,
    "Attunement": "Attunement",
    "Other Possessions": "Other Possessions",
    "Special": "Special"
  };
  console.log("Rendering Char View Inv");
  
  // console.log(reloading);
  // console.log(search_string);
  // console.log(view);
  // console.log(edit_view);
  // console.log(drawer);
  // console.log(details_item);
  // console.log(details_action);
  return (
    <Grid container spacing={0} direction="row" className="curved_border bg_black_transp" 
      style={{
        fontSize: "11px"
      }}>
      <Grid size={6}>
        <div
          style={{
            display: "flex",
            justifyContent: "left"
          }}>
          <Grid container spacing={0} direction="row" 
            style={{
              color: (Math.round(character.weight_carried*100)/100 > character.max_carrying_capacity ? "red" : "inherit")
            }}>
            <Grid size={12}
              style={{
                fontSize: "13px",
                fontWeight: "bold",
              }}>
              Weight Carried: { Math.round(character.weight_carried*100)/100 } lb. 
            </Grid>
            <Grid size={12}
              style={{ fontWeight: "bold" }}>
              { character.encumbrance_level === 3 ?
                <span style={{ color: "red" }}>Over Carrying Capacity</span>
              : character.encumbrance_level === 2 ?
                <span style={{ color: "orange" }}>Heavily Encumbered</span>
              : character.encumbrance_level === 1 ?
                <span style={{ color: "#f3b65a" }}>Lightly Encumbered</span>
              : Math.round(character.weight_carried*100)/100 > character.max_carrying_capacity ? 
                <span style={{ color: "red" }}>Over Carrying Capacity</span>
              :
                "Unencumbered"
              }
            </Grid>
          </Grid>
        </div>
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        { renderCurrency() }
      </Grid>
      { !disabled &&
        <>
          <Grid size={2}>
          </Grid>
          <Grid size={8}>
            <CharacterManageInventoryButton
              character={character}
              disabled={!enabled}
              shared={shared}
            />
          </Grid>
          <Grid size={2}>
          </Grid>
        </>
      }
      { (character.items.filter(o => !["Equipment","Storage"].includes(o.true_id)).length === 0) ?
        <Grid size={12}>
          <CharacterStartingEquipment
            char={character}
          />
        </Grid>
      :
        <>
          <Grid size={12}>
            <div style={{ margin: "4px" }}>
              <StringBox
                name="Search Items"
                value={ search_string }
                onChange={setSearchString}
              />
            </div>
          </Grid>
          <Grid size={12}>
            <FloatMenu
              option_map={menu_option_map}
              selected={view}
              onSelect={setView}
            />
          </Grid>
          { (!["Attunement","Other"].includes(view)) &&
            renderInventory()
          }
          { (view === "ALL" || view === "Attunement") &&
            <CharacterAttunement 
              character={character} 
              disabled={disabled}           
            />
          }
          { (view === "ALL" || view === "Special") && renderSpecial() }
          { (view === "ALL" || view === "Other") &&
            renderOtherPossessions()
          }
        </>
      }
      { renderExtras() }
    </Grid>
  );
}
